import { Component, Injector, OnInit, ViewChild, ViewEncapsulation, NgZone } from '@angular/core';
import { IonRouterOutlet, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';

import { Auth } from 'src/providers/auth/auth';
import { Events } from 'src/providers/events/events';
import { TidySession } from 'src/providers/tidy-session/tidy-session';
import { RouterOutletInjector } from 'src/shared/providers/navigation/router-outlet-injector';
import { ExternalInjector } from 'src/shared/utils/external-injector';
import { GlobalSubscriptions } from './global-subscriptions';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { ThemeService } from 'src/shared/services/theme.service';
import { isOnPublicRoute } from 'src/shared/utils/public-routes';

import { translations } from 'src/shared/constants/language-translations';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { Network } from '@capacitor/network';
import { register } from 'swiper/element/bundle';

// Register Swiper custom elements (only in browser)
if (typeof document !== 'undefined' && document.documentElement) {
  register();
}

@Component({
    selector: 'app-root',
    templateUrl: 'app.html',
    styleUrls: ['./app.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class TidyHomekeeperAppPage implements OnInit {

  isAuthenticated = false;
  showAuthBackground = false;
  @ViewChild(IonRouterOutlet, { static : true }) routerOutlet: IonRouterOutlet;
  translations = translations;
  private pendingDeepLink: string | null = null;
  private isInitialized = false;

  constructor(
    private auth: Auth,
    private authModalService: AuthModalService,
    private events: Events,
    private globalSubscriptions: GlobalSubscriptions,
    private injector: Injector,
    private router: Router,
    private tidySession: TidySession,
    private routerOutletInjector: RouterOutletInjector,
    private zone: NgZone,
    private navCtrl: CustomNavController,
    private tidyStorage: TidyStorage,
    private themeService: ThemeService,
    private toastCtrl: ToastController
  ) {
    this.initDeeplinks();
  }

  async ngOnInit() {
    ExternalInjector.register(this.injector);
    await this.tidyStorage.init();

    this.globalSubscriptions.subscribe();

    this.tidySession.setup();

    this.preloadToastComponent();

    this.preloadWebPlugins();

    this.subscribeAuthEvents();

    this.routerOutletInjector.setup(this.routerOutlet);

    // Mark as initialized and process any pending deep link
    this.isInitialized = true;
    if (this.pendingDeepLink) {
      this.handleDeepLinkNavigation(this.pendingDeepLink);
      this.pendingDeepLink = null;
    }
  }

  // Ionic downloads each overlay's code the first time one is created. On the web app that
  // download fails offline, so toasts shown offline (e.g. "You need internet to go to the
  // dashboard") never appeared. Creating a hidden toast at startup loads ion-toast while the
  // app is still online; it is removed without being presented.
  private async preloadToastComponent(): Promise<void> {
    try {
      const toast = await this.toastCtrl.create({ animated: false });
      toast.remove();
    } catch (err) {
      console.warn('Could not preload ion-toast', err);
    }
  }

  // Capacitor downloads a plugin's web code the first time the plugin is called, and on the web
  // app a download that failed offline is never retried until the page reloads. The shared job
  // and shared to-do list links call these plugins when the pro starts, finishes or completes,
  // so load them at startup while the app is still online.
  private preloadWebPlugins(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }
    Device.getId().catch(() => null);
    Network.getStatus().catch(() => null);
    Geolocation.checkPermissions().catch(() => null);
  }

  initDeeplinks() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const appPath = this.parseDeepLinkPath(event.url);

        if (this.isInitialized) {
          this.handleDeepLinkNavigation(appPath);
        } else {
          this.pendingDeepLink = appPath || 'login';
        }
      });
    });
  }

  // Extracts the in-app route from a deep-link URL. Verified app links strip the
  // URL fragment, so the same route can arrive either as a hash route
  // (https://pro.tidy.com/#/password-update/<token>) when the fragment survives,
  // or as a bare path (https://pro.tidy.com/password-update/<token>) once the OS
  // has claimed and stripped it. Parse both shapes instead of splitting on a
  // hardcoded 'pro.tidy.com/#/' - otherwise the stripped-fragment case falls
  // through to the homepage and the token never reaches the page.
  private parseDeepLinkPath(url: string | undefined): string | undefined {
    if (!url) {
      return undefined;
    }
    // Strip scheme + authority (host + optional port). Matches http(s):// and any
    // custom app scheme alike, leaving everything from the first path slash on.
    let path = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
    // Drop an optional leading hash-route marker ('/#', '#') left by hash routing.
    path = path.replace(/^\/?#/, '');
    // Trim surrounding slashes.
    path = path.replace(/^\/+|\/+$/g, '');
    return path || undefined;
  }

  private handleDeepLinkNavigation(appPath: string | undefined) {
    if (appPath) {
      this.navCtrl.navigateForward(appPath);
    } else {
      this.navCtrl.navigateForward('login');
    }
  }

  async subscribeAuthEvents() {
    this.events.subscribe('auth:authenticated', () => {
      this.isAuthenticated = true;
      this.showAuthBackground = false;
    });
    this.events.subscribe('auth:logout', () => {
      this.isAuthenticated = false;
      // On a public/logged-out page (e.g. the plain login form), don't mount the
      // branded background + signup modal over it - any session event would
      // otherwise bounce the user off the login page mid-typing (Issue 52).
      if (isOnPublicRoute()) {
        this.showAuthBackground = false;
        return;
      }
      this.showAuthBackground = true;
      this.authModalService.openAuthModal('signup');
    });

    // Check initial auth state
    if (this.auth.isAuthenticated()) {
      this.isAuthenticated = true;
      this.showAuthBackground = false;
    } else if (isOnPublicRoute()) {
      // On any public route (login, token login, introduction, forgot-password,
      // create-account, password-update, email/): let that page render and
      // handle its own auth. Don't show the mock dashboard background or the
      // signup modal on top of it (Issue 52 - previously only the token-login
      // variant `#/login/:token` was allowlisted, so the plain `#/login` page
      // got the overlay).
      this.showAuthBackground = false;
    } else {
      this.showAuthBackground = true;
      this.authModalService.openAuthModal('signup');
    }
  }
}
